import { useMemo, useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AlertCircle, Plus, Trash2 } from 'lucide-react';
import {
  Heading,
  BodyText,
  MetricCard,
  METRIC_READ_CAPTIONS,
  Card,
  Button,
  Skeleton,
  Switch,
  Dialog,
  Input,
  Tabs,
  TabPanel,
  Select,
  Alert,
  FormLabel,
} from '@aaimplatform/ui';
import type { TenantDetail } from '../api/client';
import { useTenant, useUpdateTenant } from '../hooks/useTenants';
import { useAuthStore } from '../stores/auth';
import { hasAnyRole, PLATFORM_ADMIN_EQUIVALENT_ROLES } from '../utils/role-helpers';
import {
  useTenantEntitlements,
  useUpdateTenantEntitlements,
  type TenantEntitlements as TenantEntitlementsType,
  type TenantEntitlementsUpdate,
} from '../hooks/useTenantEntitlements';
import {
  useTenantNotificationConfigById,
  useUpdateTenantNotificationConfigById,
  useTenantSlaConfig,
  useUpdateTenantSlaConfig,
  useTenantReviewerConfig,
  useUpdateTenantReviewerConfig,
} from '../hooks/useTenantConfig';
import type {
  TenantNotificationConfig,
  TenantNotificationChannel,
  WebhookChannelConfig,
  TenantSlaConfig,
  TenantReviewerConfig,
  TenantReviewerAssignmentRule,
  TenantReviewerSizeThresholds,
} from '../api/services/tenant-config';
import type { components } from '@aaimplatform/institutional-types/platform-admin';
import { getTypeLabel } from '../utils/institution-labels';
import {
  ABSENT,
  formatCount,
  formatDate,
  formatDateTime,
  formatMoneyCompact,
  formatPercentValue,
} from '../utils/display-format';
import { PalStateBadge, TenantPurposeBadge, TenantStatusBadge } from '../utils/status-labels';
import { RouterBackLink } from '../components/RouterBackLink';
import { RouterTableLink } from '../components/RouterTableLink';
import { INTEGRATION_CATEGORY_LABEL } from './Integrations';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type PalCollateralType = NonNullable<components['schemas']['TenantPALSettings']['allowedCollateralTypes']>[number];

/**
 * One label per member of the TenantPALSettings.allowedCollateralTypes enum,
 * so a value the contract adds is a tsc error here until it has a label. The
 * chips printed the codes with underscores swapped for spaces ("FIXED INCOME").
 */
export const PAL_COLLATERAL_TYPE_LABEL: Record<PalCollateralType, string> = {
  EQUITY: 'Equity',
  FIXED_INCOME: 'Fixed income',
  ETF: 'ETF',
  MUTUAL_FUND: 'Mutual fund',
  REAL_ESTATE: 'Real estate',
};

function isReported(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function getIntegrationStatusColor(status: string): string {
  switch (status) {
    case 'CONNECTED':
      return 'bg-growth/10 text-growth';
    case 'CONFIGURED':
      return 'bg-warning/10 text-warning';
    case 'ERROR':
      return 'bg-error/10 text-error';
    default:
      return 'bg-quaternary/10 text-quaternary';
  }
}

function isWebhookChannel(
  channel: TenantNotificationChannel,
): channel is TenantNotificationChannel & { config: WebhookChannelConfig } {
  return channel.type === 'webhook';
}

// ---------------------------------------------------------------------------
// Section primitives
// ---------------------------------------------------------------------------

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="block text-label-xs font-sans font-semibold uppercase text-tertiary leading-normal mb-1">
      {children}
    </span>
  );
}

function SectionValue({ children, mono }: { children: React.ReactNode; mono?: boolean }) {
  return (
    <span className={`text-sm ${mono ? 'font-mono' : 'font-sans'} text-primary leading-normal`}>
      {children}
    </span>
  );
}

function SettingsRow({
  label,
  description,
  children,
}: {
  label: string;
  description?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex justify-between items-center py-3 border-b border-recessed last:border-0">
      <div>
        <BodyText variant="ui" className="font-medium">{label}</BodyText>
        {description && (
          <BodyText variant="ui" className="text-sm text-tertiary">{description}</BodyText>
        )}
      </div>
      {children}
    </div>
  );
}

/**
 * An informational notice: no error or warning semantic, so it carries the
 * sanctioned neutral callout treatment (no-accent-rail-on-cards.md) and
 * secondary text. It rendered with a red border and red text, which read as an
 * alert on a page with nothing wrong.
 */
function PlaceholderNotice({ message }: { message: React.ReactNode }) {
  return (
    <div className="bg-accent/5 border border-recessed rounded-sm p-4">
      <BodyText variant="ui" className="text-secondary text-sm">{message}</BodyText>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Primary contact (operator edit, GH #4155)
// ---------------------------------------------------------------------------

const PRIMARY_CONTACT_FORM_ID = 'tenant-primary-contact-form';

/**
 * The operator's edit of an institution's primary contact. It sends
 * UpdateTenantRequest.primaryContact alone through PATCH /tenants/{tenantId},
 * the route's platform-admin path; PR #4101 (GH #673) makes the route refuse
 * the field from the FI side. The address passes the browser's email
 * constraint (UpdateTenantRequest declares `format: email`) before it is sent.
 * The dialog cannot be dismissed while a save is in flight, so a refusal is
 * shown in the dialog that sent it.
 */
function PrimaryContactDialog({
  open,
  onOpenChange,
  tenant,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenant: TenantDetail;
}) {
  const current = tenant.primaryContact ?? '';
  const [draft, setDraft] = useState(current);
  const [fieldError, setFieldError] = useState('');
  const [saveError, setSaveError] = useState('');
  const updateMutation = useUpdateTenant(tenant.id);

  useEffect(() => {
    if (open) {
      setDraft(current);
      setFieldError('');
      setSaveError('');
    }
  }, [open, current]);

  const nextContact = draft.trim();
  const unchanged = nextContact === current;

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!event.currentTarget.checkValidity()) {
      setFieldError('Enter a valid email address.');
      return;
    }
    setSaveError('');
    try {
      await updateMutation.mutateAsync({ primaryContact: nextContact });
      onOpenChange(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Unknown error');
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Edit Primary Contact"
      size="md"
      isDismissable={!updateMutation.isPending}
      isKeyboardDismissDisabled={updateMutation.isPending}
      showCloseButton={!updateMutation.isPending}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={updateMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form={PRIMARY_CONTACT_FORM_ID}
            disabled={unchanged || updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Contact'}
          </Button>
        </>
      }
    >
      <form id={PRIMARY_CONTACT_FORM_ID} onSubmit={(event) => void handleSubmit(event)} noValidate>
        <Input
          id="tenant-primary-contact"
          type="email"
          label="Primary Contact Email"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setFieldError('');
          }}
          placeholder="admin@institution.org"
          required
          error={fieldError || undefined}
        />
      </form>
      {saveError && (
        <Alert type="error" role="alert" className="mt-4">
          Failed to save: {saveError}
        </Alert>
      )}
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Tab: Overview (existing single-page sections)
// ---------------------------------------------------------------------------

function TenantOverviewSection({ tenant }: { tenant: TenantDetail }) {
  // GH #4155: only a platform-admin role sees the primary contact edit. An
  // impersonation session carries the institution's roles (`fi_admin`), and
  // with PR #4101 the route refuses the field from the FI side, so the control
  // is not offered there. The route decides; this keeps the page from offering
  // an edit it would refuse.
  const roles = useAuthStore((s) => s.user?.roles);
  const canEditContact = hasAnyRole(roles, PLATFORM_ADMIN_EQUIVALENT_ROLES);
  const [contactEditorOpen, setContactEditorOpen] = useState(false);

  return (
    <>
      {/* Header: Name, status badges */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-6">
        <div>
          {/* GH #1836: the operator must be able to tell WHICH tenant they
              are editing from the page itself, on every tab -- this section
              renders above the tab switcher. Tenant.name is required on the
              canonical schema, so no fallback copy is fabricated. */}
          {/* The layout's header bar is the page h1, so the tenant name is the
              first h2 beneath it. */}
          <Heading level={2}>{tenant.name}</Heading>
          <BodyText variant="ui" className="text-tertiary mt-1">
            {/* charterNumber is optional in the contract and omitted when the
                institution record carries none (ruling
                absence-representable-contracts); the absence marker holds its
                place, so the line never reads "| Credit Union". */}
            {`${tenant.charterNumber ? tenant.charterNumber : ABSENT} | `}
            {getTypeLabel(tenant.type)}
          </BodyText>
          {canEditContact ? (
            <div className="flex items-center gap-2 mt-0.5">
              {/* An operator sees the line with no contact on file too, so
                  the edit has a place; the absence marker holds it. */}
              <BodyText variant="ui" className="text-tertiary text-sm">
                Contact: {tenant.primaryContact ? tenant.primaryContact : ABSENT}
              </BodyText>
              <Button variant="tertiary" size="small" onClick={() => setContactEditorOpen(true)}>
                Edit contact
              </Button>
            </div>
          ) : (
            tenant.primaryContact && (
              <BodyText variant="ui" className="text-tertiary text-sm mt-0.5">
                Contact: {tenant.primaryContact}
              </BodyText>
            )
          )}
          {tenant.createdAt && (
            <BodyText variant="ui" className="text-tertiary text-sm mt-0.5">
              Onboarded: {formatDate(tenant.createdAt)}
            </BodyText>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Both header chips compose the kit Badge, so they share one shape;
              the PAL chip was drawn from raw classes with a border. */}
          <TenantStatusBadge status={tenant.status} />
          {/* Tenant.purpose is required (operator ruling 2026-10-22). */}
          <TenantPurposeBadge purpose={tenant.purpose} />
          <PalStateBadge enabled={tenant.palEnabled} prefixed />
        </div>
      </div>

      {/* Metric cards. Each figure is optional on the tenant record
          (Tenant.activeLoans / totalExposure / avgLtv, TenantDetail.stats).
          An absent figure prints "--" with "Not reported"; none is coerced
          to a measured 0, and a present 0 prints as 0. */}
      <TenantMetricCards tenant={tenant} />

      {canEditContact && (
        <PrimaryContactDialog
          open={contactEditorOpen}
          onOpenChange={setContactEditorOpen}
          tenant={tenant}
        />
      )}
    </>
  );
}

export function TenantMetricCards({ tenant }: { tenant: TenantDetail }) {
  const activeLoans = tenant.activeLoans ?? tenant.stats?.activeLoans;
  const totalLoans = tenant.stats?.totalLoans;
  const totalExposure = tenant.totalExposure;
  const totalCollateral = tenant.stats?.totalCollateral;
  const avgLtv = tenant.avgLtv ?? tenant.stats?.avgLtv;
  const totalMembers = tenant.stats?.totalMembers;
  const notReported = METRIC_READ_CAPTIONS.notReported;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
      <MetricCard
        label="Active Loans"
        value={formatCount(activeLoans)}
        caption={isReported(activeLoans) ? undefined : notReported}
        description={isReported(totalLoans) ? `${formatCount(totalLoans)} total` : undefined}
      />
      <MetricCard
        label="Total Exposure"
        value={formatMoneyCompact(totalExposure)}
        caption={isReported(totalExposure) ? undefined : notReported}
        description={isReported(totalExposure) ? 'Across active loans' : undefined}
      />
      <MetricCard
        label="Total Collateral"
        value={formatMoneyCompact(totalCollateral)}
        caption={isReported(totalCollateral) ? undefined : notReported}
        description={isReported(totalCollateral) ? 'Pledged assets' : undefined}
      />
      <MetricCard
        label="Avg LTV"
        value={formatPercentValue(avgLtv)}
        caption={isReported(avgLtv) ? undefined : notReported}
        description={
          isReported(totalMembers) ? `${formatCount(totalMembers)} members` : undefined
        }
      />
    </div>
  );
}

function LendingConfigSection({ tenant }: { tenant: TenantDetail }) {
  const settings = tenant.settings;
  const hasSettings = settings && (
    settings.ltvLimit !== undefined ||
    settings.collateralAlertThreshold !== undefined ||
    settings.allowedCollateralTypes !== undefined
  );

  return (
    <Card className="mb-6 p-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
        <Heading level={3} size="card">
          Lending Configuration
        </Heading>
        <BodyText variant="ui" className="text-xs text-tertiary font-sans">
          Read-only here. Platform-wide lending defaults are set under{' '}
          {/* The kit link in the secondary color; red is reserved for CTAs
              and alerts, not a navigation link inside a note. */}
          <RouterTableLink to="/platform-config/lending">Lending Defaults</RouterTableLink>
          .
        </BodyText>
      </div>

      {hasSettings ? (
        <div className="space-y-0">
          <SettingsRow
            label="Maximum LTV Ratio"
            description="Loan-to-value ceiling for pledged-asset loans"
          >
            <SectionValue mono>{formatPercentValue(settings.ltvLimit)}</SectionValue>
          </SettingsRow>

          <SettingsRow
            label="Collateral Alert Threshold"
            description="LTV percentage that triggers a collateral deficiency alert"
          >
            <SectionValue mono>
              {formatPercentValue(settings.collateralAlertThreshold)}
            </SectionValue>
          </SettingsRow>

          <SettingsRow
            label="Automatic Collateral Alerts"
            description="Send automated collateral deficiency notifications"
          >
            <Switch
              isSelected={settings.autoCollateralAlert ?? false}
              isReadOnly
              size="lg"
            />
          </SettingsRow>

          {settings.allowedCollateralTypes &&
            settings.allowedCollateralTypes.length > 0 && (
              <SettingsRow
                label="Allowed Collateral Types"
                description="Asset classes eligible for pledge"
              >
                <div className="flex flex-wrap gap-1.5">
                  {settings.allowedCollateralTypes.map((ct) => (
                    <span
                      key={ct}
                      className="inline-flex px-2 py-0.5 text-xs font-sans font-medium rounded-sm bg-recessed text-secondary"
                    >
                      {PAL_COLLATERAL_TYPE_LABEL[ct]}
                    </span>
                  ))}
                </div>
              </SettingsRow>
            )}

          <SettingsRow label="PAL Service">
            <Switch isSelected={tenant.palEnabled} isReadOnly size="lg" />
          </SettingsRow>
        </div>
      ) : (
        <PlaceholderNotice message="No lending configuration was returned with this tenant record." />
      )}
    </Card>
  );
}

function MonitoringThresholdsSection({ tenant }: { tenant: TenantDetail }) {
  const settings = tenant.settings;
  const hasThresholds = settings && (
    settings.ltvLimit !== undefined ||
    settings.collateralAlertThreshold !== undefined
  );

  return (
    <Card className="mb-6 p-6">
      <Heading level={3} size="card" className="mb-4">
        Monitoring Thresholds
      </Heading>

      {hasThresholds ? (
        <div className="space-y-0">
          <SettingsRow
            label="LTV Warning Level"
            description="Alert when a loan's LTV approaches this threshold"
          >
            <SectionValue mono>
              {formatPercentValue(settings.collateralAlertThreshold)}
            </SectionValue>
          </SettingsRow>

          <SettingsRow
            label="LTV Critical Level"
            description="Maximum LTV before collateral call is triggered"
          >
            <SectionValue mono>{formatPercentValue(settings.ltvLimit)}</SectionValue>
          </SettingsRow>

          <SettingsRow
            label="Automatic Alerts"
            description="Send notifications when thresholds are breached"
          >
            <Switch
              isSelected={settings.autoCollateralAlert ?? false}
              isReadOnly
              size="lg"
            />
          </SettingsRow>
        </div>
      ) : (
        <PlaceholderNotice message="No monitoring thresholds were returned with this tenant record. They derive from the lending configuration." />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Notifications tab (webhook editor, digest preview)
// ---------------------------------------------------------------------------

type WebhookChannelDraft = Pick<components['schemas']['TenantNotificationChannel'], 'id' | 'displayName' | 'enabled'> & components['schemas']['WebhookChannelConfig'];

function maskHmacRef(ref: string): string {
  if (!ref) return 'Not set';
  if (ref.length <= 16) return ref;
  return `${ref.slice(0, 8)}...${ref.slice(-8)}`;
}

function WebhookEditorDialog({
  open,
  onOpenChange,
  tenantId,
  config,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string;
  config: TenantNotificationConfig;
}) {
  const existingWebhookChannels = useMemo<TenantNotificationChannel[]>(
    () => config.notificationChannels.filter(isWebhookChannel),
    [config.notificationChannels],
  );

  const [drafts, setDrafts] = useState<WebhookChannelDraft[]>(() =>
    existingWebhookChannels.map((channel) => {
      const webhookConfig = channel.config as WebhookChannelConfig;
      return {
        id: channel.id,
        displayName: channel.displayName,
        enabled: channel.enabled,
        url: webhookConfig.url,
        hmacSecretRef: webhookConfig.hmacSecretRef,
      };
    }),
  );

  useEffect(() => {
    if (open) {
      setDrafts(
        existingWebhookChannels.map((channel) => {
          const webhookConfig = channel.config as WebhookChannelConfig;
          return {
            id: channel.id,
            displayName: channel.displayName,
            enabled: channel.enabled,
            url: webhookConfig.url,
            hmacSecretRef: webhookConfig.hmacSecretRef,
          };
        }),
      );
    }
  }, [open, existingWebhookChannels]);

  const updateMutation = useUpdateTenantNotificationConfigById(tenantId);

  function updateDraft(id: string, patch: Partial<WebhookChannelDraft>) {
    setDrafts((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));
  }

  async function handleSave() {
    const nonWebhookChannels = config.notificationChannels.filter(
      (channel) => !isWebhookChannel(channel),
    );
    const updatedWebhookChannels: TenantNotificationChannel[] = drafts.map(
      (draft): TenantNotificationChannel => {
        const original = existingWebhookChannels.find((c) => c.id === draft.id);
        const webhookConfig: WebhookChannelConfig = {
          url: draft.url,
          hmacSecretRef: draft.hmacSecretRef,
        };
        return {
          id: draft.id,
          type: 'webhook',
          displayName: draft.displayName,
          enabled: draft.enabled,
          config: webhookConfig,
          filter: original?.filter,
        };
      },
    );

    const nextConfig: TenantNotificationConfig = {
      notificationChannels: [...nonWebhookChannels, ...updatedWebhookChannels],
      digestRecipients: config.digestRecipients,
      digestEnabled: config.digestEnabled,
      digestTimeLocal: config.digestTimeLocal,
      digestTimezone: config.digestTimezone,
    };

    await updateMutation.mutateAsync(nextConfig);
    onOpenChange(false);
  }

  const hasWebhooks = drafts.length > 0;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Edit Webhook Channels"
      size="xl"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={updateMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!hasWebhooks || updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </>
      }
    >
      {hasWebhooks ? (
        <div className="space-y-6">
          {drafts.map((draft) => (
            <div
              key={draft.id}
              className="border border-recessed rounded-sm p-4 space-y-3"
            >
              <div className="flex justify-between items-center">
                <BodyText variant="ui" className="font-medium">
                  {draft.displayName || '(Unnamed webhook)'}
                </BodyText>
                <Switch
                  isSelected={draft.enabled}
                  onChange={(next) => updateDraft(draft.id, { enabled: next })}
                  size="lg"
                  label={draft.enabled ? 'Enabled' : 'Disabled'}
                />
              </div>
              <div>
                <SectionLabel>Display Name</SectionLabel>
                <Input
                  value={draft.displayName}
                  onChange={(e) =>
                    updateDraft(draft.id, { displayName: e.target.value })
                  }
                  placeholder="e.g. Pledge Events"
                />
              </div>
              <div>
                <SectionLabel>Webhook URL</SectionLabel>
                <Input
                  value={draft.url}
                  onChange={(e) => updateDraft(draft.id, { url: e.target.value })}
                  placeholder="https://example.com/webhooks/pledge"
                />
              </div>
              <div>
                <SectionLabel>HMAC Secret Reference</SectionLabel>
                <Input
                  value={draft.hmacSecretRef}
                  onChange={(e) =>
                    updateDraft(draft.id, { hmacSecretRef: e.target.value })
                  }
                  placeholder="projects/aaim-core-infra/secrets/tenant-webhook-hmac-..."
                />
                <BodyText variant="ui" className="text-xs text-tertiary mt-1">
                  Secret Manager reference only. Do NOT paste the literal secret value.
                </BodyText>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <PlaceholderNotice message="No webhook channels are configured for this tenant. Webhook provisioning is managed through the tenant onboarding API." />
      )}
      {updateMutation.isError && (
        <div className="mt-4 bg-error/5 border border-error/20 rounded-sm p-3">
          <BodyText variant="ui" className="text-error text-sm">
            Failed to save: {updateMutation.error?.message ?? 'Unknown error'}
          </BodyText>
        </div>
      )}
    </Dialog>
  );
}

function NotificationsTab({ tenant }: { tenant: TenantDetail }) {
  const [editorOpen, setEditorOpen] = useState(false);
  const {
    data: notificationConfig,
    isLoading: configLoading,
    error: configError,
  } = useTenantNotificationConfigById(tenant.id);

  const integrations = tenant.integrations ?? [];
  const hasIntegrations = integrations.length > 0;

  const webhookChannels = useMemo<TenantNotificationChannel[]>(
    () =>
      notificationConfig?.notificationChannels?.filter(isWebhookChannel) ?? [],
    [notificationConfig],
  );

  return (
    <Card className="mb-6 p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
        <Heading level={3} size="card">
          Integrations and Webhook Endpoints
        </Heading>
        <Button
          variant="secondary"
          onClick={() => setEditorOpen(true)}
          disabled={configLoading || !notificationConfig}
        >
          Edit Webhooks
        </Button>
      </div>

      {hasIntegrations ? (
        <div className="space-y-0">
          {integrations.map((integration) => (
            <div
              key={integration.integrationId}
              className="flex justify-between items-center py-3 border-b border-recessed last:border-0"
            >
              <div>
                <BodyText variant="ui" className="font-medium">
                  {integration.name}
                </BodyText>
                <BodyText variant="ui" className="text-sm text-tertiary">
                  {INTEGRATION_CATEGORY_LABEL[integration.type]}
                  {integration.lastSyncAt
                    ? ` | Last sync: ${formatDateTime(integration.lastSyncAt)}`
                    : ''}
                </BodyText>
              </div>
              <span
                className={`inline-flex px-2 py-1 text-xs font-sans font-semibold rounded-sm leading-normal ${getIntegrationStatusColor(integration.status)}`}
              >
                {integration.status}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <PlaceholderNotice message="No integrations configured for this tenant. Integration management is part of tenant onboarding." />
      )}

      <div className="mt-6 pt-4 border-t border-recessed">
        <Heading level={4} size="card" className="mb-3 text-sm">
          Webhook Channels
        </Heading>

        {configLoading && (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        )}

        {configError && (
          <div className="bg-error/5 border border-error/20 rounded-sm p-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-error flex-shrink-0" />
            <BodyText variant="ui" className="text-error text-sm">
              Failed to load webhook configuration: {configError.message}
            </BodyText>
          </div>
        )}

        {!configLoading && !configError && webhookChannels.length === 0 && (
          <PlaceholderNotice message="No webhook channels are configured for this tenant." />
        )}

        {!configLoading && !configError && webhookChannels.length > 0 && (
          <div className="space-y-0">
            {webhookChannels.map((channel) => {
              const webhookConfig = channel.config as WebhookChannelConfig;
              return (
                <div
                  key={channel.id}
                  className="py-3 border-b border-recessed last:border-0"
                >
                  <div className="flex justify-between items-start">
                    <div className="min-w-0 flex-1">
                      <BodyText variant="ui" className="font-medium">
                        {channel.displayName}
                      </BodyText>
                      <BodyText
                        variant="ui"
                        className="text-xs font-mono text-tertiary break-all mt-0.5"
                      >
                        {webhookConfig.url}
                      </BodyText>
                      <BodyText variant="ui" className="text-xs text-tertiary mt-0.5">
                        HMAC: {maskHmacRef(webhookConfig.hmacSecretRef)}
                      </BodyText>
                    </div>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-sans font-semibold rounded-sm leading-normal ml-3 flex-shrink-0 ${
                        channel.enabled
                          ? 'bg-growth/10 text-growth'
                          : 'bg-recessed text-secondary'
                      }`}
                    >
                      {channel.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Digest configuration (read-only summary -- editable channels live in dialog) */}
      {!configLoading && !configError && notificationConfig && (
        <div className="mt-6 pt-4 border-t border-recessed">
          <Heading level={4} size="card" className="mb-3 text-sm">
            Daily Digest
          </Heading>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <SectionLabel>Digest Enabled</SectionLabel>
              <SectionValue>
                {notificationConfig.digestEnabled ? 'Yes' : 'No'}
              </SectionValue>
            </div>
            <div>
              <SectionLabel>Local Time</SectionLabel>
              <SectionValue mono>
                {notificationConfig.digestTimeLocal ?? '--'}
              </SectionValue>
            </div>
            <div>
              <SectionLabel>Timezone</SectionLabel>
              <SectionValue>
                {notificationConfig.digestTimezone ?? '--'}
              </SectionValue>
            </div>
          </div>
          <div className="mt-3">
            <SectionLabel>Digest Recipients</SectionLabel>
            {notificationConfig.digestRecipients &&
            notificationConfig.digestRecipients.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 mt-1">
                {notificationConfig.digestRecipients.map((email) => (
                  <span
                    key={email}
                    className="inline-flex px-2 py-0.5 text-xs font-mono rounded-sm bg-recessed text-secondary"
                  >
                    {email}
                  </span>
                ))}
              </div>
            ) : (
              <SectionValue>No recipients configured</SectionValue>
            )}
          </div>
        </div>
      )}

      {notificationConfig && (
        <WebhookEditorDialog
          open={editorOpen}
          onOpenChange={setEditorOpen}
          tenantId={tenant.id}
          config={notificationConfig}
        />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// SLA tab
// ---------------------------------------------------------------------------

function SlaTab({ tenantId }: { tenantId: string }) {
  const {
    data: slaConfig,
    isLoading,
    error,
  } = useTenantSlaConfig(tenantId);
  const updateMutation = useUpdateTenantSlaConfig(tenantId);

  // Form draft -- seeded from server data once loaded.
  const [greenHours, setGreenHours] = useState<string>('');
  const [yellowHours, setYellowHours] = useState<string>('');
  const [timezone, setTimezone] = useState<string>('');
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (slaConfig) {
      setGreenHours(String(slaConfig.greenHours));
      setYellowHours(String(slaConfig.yellowHours));
      setTimezone(slaConfig.timezone);
    }
  }, [slaConfig]);

  const isDirty = useMemo(() => {
    if (!slaConfig) return false;
    return (
      String(slaConfig.greenHours) !== greenHours ||
      String(slaConfig.yellowHours) !== yellowHours ||
      slaConfig.timezone !== timezone
    );
  }, [slaConfig, greenHours, yellowHours, timezone]);

  // Local validation (server validates again).
  const greenNum = Number(greenHours);
  const yellowNum = Number(yellowHours);
  const localValidationError = useMemo(() => {
    if (!Number.isFinite(greenNum) || greenNum < 1) {
      return 'Green hours must be a positive integer.';
    }
    if (!Number.isFinite(yellowNum) || yellowNum < 1) {
      return 'Yellow hours must be a positive integer.';
    }
    if (yellowNum <= greenNum) {
      return 'Yellow hours must be greater than green hours.';
    }
    if (!timezone.trim()) {
      return 'Timezone is required.';
    }
    return null;
  }, [greenNum, yellowNum, timezone]);

  async function handleSave() {
    if (localValidationError) return;
    const body: TenantSlaConfig = {
      greenHours: Math.trunc(greenNum),
      yellowHours: Math.trunc(yellowNum),
      timezone: timezone.trim(),
    };
    await updateMutation.mutateAsync(body);
    setSavedAt(new Date());
  }

  if (isLoading) {
    return (
      <Card className="p-6">
        <Skeleton className="h-5 w-40 mb-4" />
        <Skeleton className="h-12 w-full mb-3" />
        <Skeleton className="h-12 w-full mb-3" />
        <Skeleton className="h-12 w-full" />
      </Card>
    );
  }

  if (error) {
    return (
      <Alert type="error" className="mb-4">
        Failed to load SLA configuration: {error.message}
      </Alert>
    );
  }

  return (
    <Card className="p-6">
      <Heading level={3} size="card" className="mb-2">
        SLA Thresholds
      </Heading>
      <BodyText variant="ui" className="text-tertiary text-sm mb-6">
        Tasks below the green threshold are healthy. Between green and yellow
        they are warning. At or above yellow they are over-SLA. Server enforces
        green {'<'} yellow.
      </BodyText>

      {savedAt && !updateMutation.isPending && !updateMutation.isError && (
        <Alert type="success" className="mb-4">
          SLA configuration saved at {savedAt.toLocaleTimeString()}.
        </Alert>
      )}

      {updateMutation.isError && (
        <Alert type="error" className="mb-4">
          {updateMutation.error?.message ?? 'Failed to save SLA configuration.'}
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div>
          <FormLabel>Green Hours</FormLabel>
          <Input
            type="number"
            min={1}
            value={greenHours}
            onChange={(e) => setGreenHours(e.target.value)}
            placeholder="24"
          />
          <BodyText variant="ui" className="text-xs text-tertiary mt-1">
            Upper bound of the green age band.
          </BodyText>
        </div>
        <div>
          <FormLabel>Yellow Hours</FormLabel>
          <Input
            type="number"
            min={1}
            value={yellowHours}
            onChange={(e) => setYellowHours(e.target.value)}
            placeholder="72"
          />
          <BodyText variant="ui" className="text-xs text-tertiary mt-1">
            Upper bound of the yellow age band. Must exceed green.
          </BodyText>
        </div>
        <div>
          <FormLabel>Timezone</FormLabel>
          <Input
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            placeholder="America/New_York"
          />
          <BodyText variant="ui" className="text-xs text-tertiary mt-1">
            IANA timezone identifier.
          </BodyText>
        </div>
      </div>

      {localValidationError && (
        <div className="mb-4 bg-warning/5 border border-warning/30 rounded-sm p-3">
          <BodyText variant="ui" className="text-warning text-sm">
            {localValidationError}
          </BodyText>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-4 border-t border-recessed">
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={
            !isDirty || updateMutation.isPending || Boolean(localValidationError)
          }
        >
          {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Reviewers tab
// ---------------------------------------------------------------------------

const REVIEWER_RULE_OPTIONS: ReadonlyArray<{
  value: TenantReviewerAssignmentRule;
  label: string;
}> = [
  { value: 'default_only', label: 'Default reviewer only' },
  { value: 'round_robin', label: 'Round robin' },
  { value: 'by_package_size', label: 'By package size' },
];

function ReviewersTab({ tenantId }: { tenantId: string }) {
  const {
    data: reviewerConfig,
    isLoading,
    error,
  } = useTenantReviewerConfig(tenantId);
  const updateMutation = useUpdateTenantReviewerConfig(tenantId);

  const [pledgeAssignmentRule, setPledgeAssignmentRule] =
    useState<TenantReviewerAssignmentRule>('default_only');
  const [defaultReviewerId, setDefaultReviewerId] = useState<string>('');
  const [roundRobinIds, setRoundRobinIds] = useState<string[]>([]);
  const [smallReviewerId, setSmallReviewerId] = useState<string>('');
  const [largeReviewerId, setLargeReviewerId] = useState<string>('');
  const [largeThresholdUsd, setLargeThresholdUsd] = useState<string>('');
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [newRoundRobinId, setNewRoundRobinId] = useState<string>('');

  useEffect(() => {
    if (reviewerConfig) {
      setPledgeAssignmentRule(reviewerConfig.pledgeAssignmentRule);
      setDefaultReviewerId(reviewerConfig.defaultReviewerId ?? '');
      setRoundRobinIds(reviewerConfig.roundRobinReviewerIds ?? []);
      const thresholds = reviewerConfig.sizeThresholds;
      setSmallReviewerId(thresholds?.smallReviewerId ?? '');
      setLargeReviewerId(thresholds?.largeReviewerId ?? '');
      setLargeThresholdUsd(
        thresholds?.largeThresholdUsd !== undefined
          ? String(thresholds.largeThresholdUsd)
          : '',
      );
    }
  }, [reviewerConfig]);

  const isDirty = useMemo(() => {
    if (!reviewerConfig) return false;
    if (pledgeAssignmentRule !== reviewerConfig.pledgeAssignmentRule) return true;
    if ((reviewerConfig.defaultReviewerId ?? '') !== defaultReviewerId) return true;
    const existingRr = reviewerConfig.roundRobinReviewerIds ?? [];
    if (existingRr.length !== roundRobinIds.length) return true;
    for (let i = 0; i < existingRr.length; i++) {
      if (existingRr[i] !== roundRobinIds[i]) return true;
    }
    const t = reviewerConfig.sizeThresholds;
    if ((t?.smallReviewerId ?? '') !== smallReviewerId) return true;
    if ((t?.largeReviewerId ?? '') !== largeReviewerId) return true;
    const existingThreshold =
      t?.largeThresholdUsd !== undefined ? String(t.largeThresholdUsd) : '';
    if (existingThreshold !== largeThresholdUsd) return true;
    return false;
  }, [
    reviewerConfig,
    pledgeAssignmentRule,
    defaultReviewerId,
    roundRobinIds,
    smallReviewerId,
    largeReviewerId,
    largeThresholdUsd,
  ]);

  const localValidationError = useMemo(() => {
    if (!defaultReviewerId.trim()) {
      return 'Default reviewer id is required (used as fallback regardless of rule).';
    }
    if (pledgeAssignmentRule === 'round_robin' && roundRobinIds.length === 0) {
      return 'Round robin rule requires at least one reviewer id.';
    }
    if (pledgeAssignmentRule === 'by_package_size') {
      if (!smallReviewerId.trim()) {
        return 'Small reviewer id is required for by-package-size rule.';
      }
      if (!largeReviewerId.trim()) {
        return 'Large reviewer id is required for by-package-size rule.';
      }
      const thresholdNum = Number(largeThresholdUsd);
      if (!Number.isFinite(thresholdNum) || thresholdNum < 0) {
        return 'Large threshold must be a non-negative number.';
      }
    }
    return null;
  }, [
    pledgeAssignmentRule,
    defaultReviewerId,
    roundRobinIds,
    smallReviewerId,
    largeReviewerId,
    largeThresholdUsd,
  ]);

  async function handleSave() {
    if (localValidationError) return;
    const body: TenantReviewerConfig = {
      pledgeAssignmentRule,
      defaultReviewerId: defaultReviewerId.trim(),
      roundRobinReviewerIds:
        pledgeAssignmentRule === 'round_robin'
          ? roundRobinIds
          : roundRobinIds.length > 0
            ? roundRobinIds
            : undefined,
    };
    if (pledgeAssignmentRule === 'by_package_size') {
      const thresholds: TenantReviewerSizeThresholds = {
        smallReviewerId: smallReviewerId.trim(),
        largeReviewerId: largeReviewerId.trim(),
        largeThresholdUsd: Number(largeThresholdUsd),
      };
      body.sizeThresholds = thresholds;
    }
    await updateMutation.mutateAsync(body);
    setSavedAt(new Date());
  }

  function addRoundRobinId() {
    const id = newRoundRobinId.trim();
    if (id && !roundRobinIds.includes(id)) {
      setRoundRobinIds([...roundRobinIds, id]);
      setNewRoundRobinId('');
    }
  }

  function removeRoundRobinId(id: string) {
    setRoundRobinIds(roundRobinIds.filter((r) => r !== id));
  }

  if (isLoading) {
    return (
      <Card className="p-6">
        <Skeleton className="h-5 w-40 mb-4" />
        <Skeleton className="h-12 w-full mb-3" />
        <Skeleton className="h-12 w-full mb-3" />
        <Skeleton className="h-12 w-full" />
      </Card>
    );
  }

  if (error) {
    return (
      <Alert type="error" className="mb-4">
        Failed to load reviewer configuration: {error.message}
      </Alert>
    );
  }

  return (
    <Card className="p-6">
      <Heading level={3} size="card" className="mb-2">
        Reviewer Assignment
      </Heading>
      <BodyText variant="ui" className="text-tertiary text-sm mb-6">
        Strategy used to assign a reviewer to newly-created pledges. Server
        enforces rule-specific validation: round-robin requires non-empty
        reviewer ids, by-package-size requires complete thresholds.
      </BodyText>

      {savedAt && !updateMutation.isPending && !updateMutation.isError && (
        <Alert type="success" className="mb-4">
          Reviewer configuration saved at {savedAt.toLocaleTimeString()}.
        </Alert>
      )}

      {updateMutation.isError && (
        <Alert type="error" className="mb-4">
          {updateMutation.error?.message ??
            'Failed to save reviewer configuration.'}
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <FormLabel>Assignment Rule</FormLabel>
          <Select
            options={REVIEWER_RULE_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
            value={pledgeAssignmentRule}
            onChange={(e) =>
              setPledgeAssignmentRule(
                e.target.value as TenantReviewerAssignmentRule,
              )
            }
          />
          <BodyText variant="ui" className="text-xs text-tertiary mt-1">
            Routing strategy for incoming pledges.
          </BodyText>
        </div>
        <div>
          <FormLabel>Default Reviewer Id</FormLabel>
          <Input
            value={defaultReviewerId}
            onChange={(e) => setDefaultReviewerId(e.target.value)}
            placeholder="reviewer-uuid-or-handle"
          />
          <BodyText variant="ui" className="text-xs text-tertiary mt-1">
            Fallback reviewer when no rule-specific assignment applies.
          </BodyText>
        </div>
      </div>

      {/* Round-robin reviewer pool */}
      <div className="mb-6 pt-4 border-t border-recessed">
        <Heading level={4} size="card" className="mb-2 text-sm">
          Round-Robin Reviewer Pool
        </Heading>
        <BodyText variant="ui" className="text-tertiary text-sm mb-3">
          Reviewer ids rotated through when the rule is round-robin. Required
          when round-robin is selected.
        </BodyText>
        <div className="flex gap-2 mb-3">
          <Input
            value={newRoundRobinId}
            onChange={(e) => setNewRoundRobinId(e.target.value)}
            placeholder="reviewer id to add"
            className="flex-1"
          />
          <Button
            variant="secondary"
            onClick={addRoundRobinId}
            disabled={!newRoundRobinId.trim()}
          >
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>
        {roundRobinIds.length > 0 ? (
          <div className="space-y-1">
            {roundRobinIds.map((id, idx) => (
              <div
                key={id}
                className="flex items-center justify-between py-2 px-3 bg-recessed/40 rounded-sm"
              >
                <span className="text-sm font-mono text-primary">
                  {idx + 1}. {id}
                </span>
                <Button
                  variant="ghost"
                  onClick={() => removeRoundRobinId(id)}
                  aria-label={`Remove reviewer ${id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <BodyText variant="ui" className="text-sm text-tertiary">
            No round-robin reviewers configured.
          </BodyText>
        )}
      </div>

      {/* By-package-size thresholds */}
      <div className="mb-6 pt-4 border-t border-recessed">
        <Heading level={4} size="card" className="mb-2 text-sm">
          By-Package-Size Thresholds
        </Heading>
        <BodyText variant="ui" className="text-tertiary text-sm mb-3">
          Pledges with totalPledgeableAmount at or above the threshold route to
          the large reviewer; below routes to the small reviewer. All three
          fields required when by-package-size is selected.
        </BodyText>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <FormLabel>Small Reviewer Id</FormLabel>
            <Input
              value={smallReviewerId}
              onChange={(e) => setSmallReviewerId(e.target.value)}
              placeholder="reviewer id"
            />
          </div>
          <div>
            <FormLabel>Large Reviewer Id</FormLabel>
            <Input
              value={largeReviewerId}
              onChange={(e) => setLargeReviewerId(e.target.value)}
              placeholder="reviewer id"
            />
          </div>
          <div>
            <FormLabel>Large Threshold (USD)</FormLabel>
            <Input
              type="number"
              min={0}
              value={largeThresholdUsd}
              onChange={(e) => setLargeThresholdUsd(e.target.value)}
              placeholder="100000"
            />
          </div>
        </div>
      </div>

      {localValidationError && (
        <div className="mb-4 bg-warning/5 border border-warning/30 rounded-sm p-3">
          <BodyText variant="ui" className="text-warning text-sm">
            {localValidationError}
          </BodyText>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-4 border-t border-recessed">
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={
            !isDirty || updateMutation.isPending || Boolean(localValidationError)
          }
        >
          {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Entitlements tab
// ---------------------------------------------------------------------------

type EntitlementFlag = keyof TenantEntitlementsType;

interface EntitlementRowSpec {
  flag: EntitlementFlag;
  label: string;
  description: string;
}

const ENTITLEMENT_ROWS: ReadonlyArray<EntitlementRowSpec> = [
  {
    flag: 'palEnabled',
    label: 'PAL Service',
    description: 'Pledged-Asset Lending consumer flow',
  },
  {
    flag: 'enableLosPreview',
    label: 'LOS Preview',
    description:
      'Lending (Preview) nav group and LOS-flavored collateral routes',
  },
  {
    flag: 'enablePeValuation',
    label: 'PE Valuation',
    description: 'Private-equity valuation nav group and API routes',
  },
  {
    flag: 'enableLoanParticipations',
    label: 'Loan Participations',
    description:
      'Loan-syndication-protocol nav group and LSP routes via gateway',
  },
  {
    flag: 'enableOverdraftPreview',
    label: 'Overdraft Coverage (Preview)',
    // The modeling half of this flag gates FOUR scenario prefixes
    // (/scenarios, /projections, /quick-projection, /institutions -- see
    // economic-modeling.routes.ts). GET /modeling/presets and
    // /modeling/presets/{type} sit OUTSIDE the gate and answer for any
    // authenticated FI caller, so the unqualified "/api/v1/modeling routes"
    // overstated what this toggle withholds -- on the screen an Aaim operator
    // uses to decide what a tenant is buying.
    description:
      'Tempo coverage policy and Economic Modeling scenarios, granted together; /api/v1/tempo and the /api/v1/modeling scenario routes (partner-runway; default off)',
  },
];

function EntitlementsTab({ tenantId }: { tenantId: string }) {
  const {
    data: entitlements,
    isLoading,
    error,
  } = useTenantEntitlements(tenantId);
  const updateMutation = useUpdateTenantEntitlements(tenantId);
  // Track which flag is mid-flight so we only disable the one being toggled.
  const [pendingFlag, setPendingFlag] = useState<EntitlementFlag | null>(null);

  async function handleToggle(flag: EntitlementFlag, next: boolean) {
    setPendingFlag(flag);
    try {
      const body: TenantEntitlementsUpdate = { [flag]: next };
      await updateMutation.mutateAsync(body);
    } finally {
      setPendingFlag(null);
    }
  }

  if (isLoading) {
    return (
      <Card className="p-6">
        <Skeleton className="h-5 w-40 mb-4" />
        <Skeleton className="h-12 w-full mb-3" />
        <Skeleton className="h-12 w-full mb-3" />
        <Skeleton className="h-12 w-full mb-3" />
        <Skeleton className="h-12 w-full" />
      </Card>
    );
  }

  if (error) {
    return (
      <Alert type="error" className="mb-4">
        Failed to load tenant entitlements: {error.message}
      </Alert>
    );
  }

  if (!entitlements) {
    return (
      <PlaceholderNotice message="Tenant entitlements are not available." />
    );
  }

  return (
    <Card className="p-6">
      <Heading level={3} size="card" className="mb-2">
        Tenant Entitlements
      </Heading>
      <BodyText variant="ui" className="text-tertiary text-sm mb-6">
        Per-tenant product-surface flags. Toggling a flag here updates both the
        backend route gate and the FI tenant&apos;s nav visibility. Each change
        writes one audit-log row server-side.
      </BodyText>

      {updateMutation.isError && (
        <Alert type="error" className="mb-4">
          {updateMutation.error?.message ??
            'Failed to update tenant entitlement.'}
        </Alert>
      )}

      <div className="space-y-0">
        {ENTITLEMENT_ROWS.map((row) => {
          const isOn = entitlements[row.flag];
          const isThisPending =
            updateMutation.isPending && pendingFlag === row.flag;
          return (
            <SettingsRow
              key={row.flag}
              label={row.label}
              description={row.description}
            >
              <Switch
                isSelected={isOn}
                isDisabled={isThisPending}
                onChange={(next) => handleToggle(row.flag, next)}
                size="lg"
                aria-label={`${row.label} entitlement`}
              />
            </SettingsRow>
          );
        })}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function TenantDetailSkeleton() {
  return (
    <div>
      {/* Back button skeleton */}
      <Skeleton className="h-9 w-36 mb-4" />

      {/* Header skeleton */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-6">
        <div>
          <Skeleton className="h-7 w-64 mb-2" />
          <Skeleton className="h-4 w-48 mb-1" />
          <Skeleton className="h-3 w-32" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Skeleton className="h-7 w-20 rounded-sm" />
          <Skeleton className="h-7 w-24 rounded-sm" />
        </div>
      </div>

      {/* Metrics skeleton */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i} className="p-6">
            <Skeleton className="h-10 w-12 mb-3" />
            <Skeleton className="h-3 w-24 mb-2" />
            <Skeleton className="h-3 w-16" />
          </Card>
        ))}
      </div>

      {/* Cards skeleton */}
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i} className="mb-6 p-6">
          <Skeleton className="h-5 w-48 mb-4" />
          <Skeleton className="h-12 w-full mb-3" />
          <Skeleton className="h-12 w-full mb-3" />
          <Skeleton className="h-12 w-full" />
        </Card>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TenantDetailPage (exported)
// ---------------------------------------------------------------------------

const TENANT_DETAIL_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'entitlements', label: 'Entitlements' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'sla', label: 'SLA' },
  { id: 'reviewers', label: 'Reviewers' },
] as const;

const TENANT_DETAIL_TABS_ID = 'tenant-detail-tabs';

const VALID_TAB_IDS = TENANT_DETAIL_TABS.map((t) => t.id) as ReadonlyArray<
  string
>;

export function TenantDetailPage() {
  const { tenantId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: tenant, isLoading, error } = useTenant(tenantId ?? '');

  // Initialize tab from ?tab= URL param if it points at a valid tab id;
  // fall back to the overview tab.
  const initialTab = useMemo(() => {
    const fromQuery = searchParams.get('tab');
    if (fromQuery && VALID_TAB_IDS.includes(fromQuery)) {
      return fromQuery;
    }
    return 'overview';
  }, [searchParams]);

  const [activeTab, setActiveTab] = useState<string>(initialTab);

  // Sync ?tab= URL param when the user changes tabs in the UI.
  function handleTabChange(nextTab: string) {
    setActiveTab(nextTab);
    const next = new URLSearchParams(searchParams);
    if (nextTab === 'overview') {
      next.delete('tab');
    } else {
      next.set('tab', nextTab);
    }
    setSearchParams(next, { replace: true });
  }

  // Keep activeTab in sync when the user navigates with a different ?tab=
  // (e.g. arriving from the Tenants list with ?tab=entitlements).
  useEffect(() => {
    const fromQuery = searchParams.get('tab');
    const next =
      fromQuery && VALID_TAB_IDS.includes(fromQuery) ? fromQuery : 'overview';
    if (next !== activeTab) {
      setActiveTab(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  if (isLoading) {
    return <TenantDetailSkeleton />;
  }

  if (error) {
    return (
      <div>
        <RouterBackLink to="/tenants">Back to Tenants</RouterBackLink>
        <div className="bg-error/5 border border-error/20 rounded-sm p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
          <div className="text-sm font-sans text-error">
            Failed to load tenant: {error.message}
          </div>
        </div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div>
        <RouterBackLink to="/tenants">Back to Tenants</RouterBackLink>
        <Card className="p-6 bg-error/10 border border-error/20">
          <BodyText variant="ui" className="text-error">
            Tenant not found.
          </BodyText>
        </Card>
      </div>
    );
  }

  return (
    <div>
      {/* Navigation */}
      <RouterBackLink to="/tenants">Back to Tenants</RouterBackLink>

      {/* Tenant header (always visible) */}
      <TenantOverviewSection tenant={tenant} />

      {/* Tab switcher */}
      <Tabs
        id={TENANT_DETAIL_TABS_ID}
        externalPanels
        tabs={TENANT_DETAIL_TABS.map((t) => ({ id: t.id, label: t.label }))}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        // Selecting a tab rewrites ?tab= and mounts a panel that fetches its
        // config and drops unsaved edits in the panel it leaves, so arrow keys
        // only move focus and Enter or Space selects.
        activation="manual"
      />

      {/* Overview tab */}
      <TabPanel tabsId={TENANT_DETAIL_TABS_ID} tabId="overview" activeTab={activeTab} className="pt-6">
        <LendingConfigSection tenant={tenant} />
        <MonitoringThresholdsSection tenant={tenant} />
      </TabPanel>

      {/* Entitlements tab -- /tenants/{tenantId}/entitlements */}
      <TabPanel tabsId={TENANT_DETAIL_TABS_ID} tabId="entitlements" activeTab={activeTab} className="pt-6">
        <EntitlementsTab tenantId={tenant.id} />
      </TabPanel>

      {/* Notifications tab -- /tenants/{tenantId}/notifications */}
      <TabPanel tabsId={TENANT_DETAIL_TABS_ID} tabId="notifications" activeTab={activeTab} className="pt-6">
        <NotificationsTab tenant={tenant} />
      </TabPanel>

      {/* SLA tab -- /tenants/{tenantId}/sla */}
      <TabPanel tabsId={TENANT_DETAIL_TABS_ID} tabId="sla" activeTab={activeTab} className="pt-6">
        <SlaTab tenantId={tenant.id} />
      </TabPanel>

      {/* Reviewers tab -- /tenants/{tenantId}/reviewers */}
      <TabPanel tabsId={TENANT_DETAIL_TABS_ID} tabId="reviewers" activeTab={activeTab} className="pt-6">
        <ReviewersTab tenantId={tenant.id} />
      </TabPanel>
    </div>
  );
}
